import React from "react";
import { CustomeDialog as DialogSlide, LoadingButton } from "../../../shared";
import { useImmer } from "use-immer";
import { Chip, FormHelperText, Stack, TextField } from "@mui/material";
import SimpleReactValidator from "simple-react-validator";
import { errorMessage } from "../../../utils";

export const SendEmailsDialog = ({
  title = "Send To",
  open,
  handleClose,
  helperText = null,
  bottomHelperText = null,
  handleFormSubmit,
  sx = {
    minWidth: "452px",
    maxWidth: { md: "80vw", xs: "95%" },
    p: "16px",
    gap: "16px",
  },
  defaultEmails = [],
  disableCloseOnBackgroundClick = false,
  submitting = false,
}) => {
  const {
    state,
    formValidator,
    handleFormChange,
    handleRemoveEmail,
    handleAddEmail,
    handleSendEmail,
  } = useSendEmailsDialog({
    open,
    defaultEmails,
    handleFormSubmit,
  });

  const helperTexts = {
    email: formValidator.current.message("email", state.email, "email"),
  };

  return (
    <DialogSlide
      open={open}
      handleClose={handleClose}
      disableCloseOnBackgroundClick={disableCloseOnBackgroundClick}
      PaperProps={{
        sx,
      }}
      title={title}
      content={
        <Stack justifyContent="space-between" gap="16px" maxWidth={420}>
          {helperText && <FormHelperText>{helperText}</FormHelperText>}
          <Stack gap="16px">
            <TextField
              name="email"
              type="email"
              label="Add Email"
              value={state.email}
              onChange={handleFormChange}
              onKeyDown={handleAddEmail}
              onBlur={handleAddEmail}
              helperText={helperTexts.email}
              error={Boolean(helperTexts.email)}
              fullWidth
              InputProps={{
                startAdornment: (
                  <Stack
                    gap="8px"
                    flexWrap="wrap"
                    flexDirection="row"
                    alignItems="center"
                    sx={{
                      maxWidth: "100%", // Ensures chips don't overflow
                      overflow: "hidden", // Prevent horizontal scroll
                      display: state.emails.length > 0 ? "flex" : "none",
                    }}
                  >
                    {state.emails.map((email, index) => (
                      <Chip
                        key={index}
                        label={email}
                        onDelete={() => handleRemoveEmail(email)}
                        sx={{
                          maxWidth: "100%",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis", // Shows ellipsis on overflow
                        }}
                      />
                    ))}
                  </Stack>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  display: "flex",
                  flexWrap: "wrap",
                  padding: "8px",
                  gap: "8px",
                  height: "auto",
                },
                "& .MuiInputBase-input": {
                  width: "auto",
                  flex: "1 1 auto",
                },
              }}
            />

            {bottomHelperText && (
              <FormHelperText>{bottomHelperText}</FormHelperText>
            )}
          </Stack>

          <LoadingButton
            type="button"
            variant="contained"
            sx={{ ml: "auto" }}
            disabled={state.emails.length === 0}
            onClick={handleSendEmail}
            loading={submitting}
          >
            Send
          </LoadingButton>
        </Stack>
      }
    />
  );
};

const useSendEmailsDialog = ({ open, defaultEmails, handleFormSubmit }) => {
  const [, setForceUpdate] = React.useState();
  const [state, setState] = useImmer({
    emails: [],
    email: "",
  });

  const formValidator = React.useRef(
    new SimpleReactValidator({
      autoForceUpdate: { forceUpdate: setForceUpdate },
      element: (message) => message,
    })
  );

  const handleFormChange = (event) => {
    setState((draft) => {
      draft.email = event.target.value;
    });
  };

  const handleAddEmail = async (event) => {
    if (event.key === "Enter" && state.email?.trim() !== "") {
      const isEmailAlreadyExist = state.emails.some(
        (email) => state.email === email
      );

      if (isEmailAlreadyExist) {
        return errorMessage("This email is already in the list.");
      }
      if (formValidator.current.allValid()) {
        const newEmails = [...state.emails, state.email];

        setState((draft) => {
          draft.emails = newEmails;
        });
        restEmail();
        return newEmails;
      } else {
        formValidator.current.showMessages();
        setForceUpdate(1);
      }
    }
  };

  const handleRemoveEmail = (value) => {
    setState((draft) => {
      draft.emails = draft.emails.filter((email) => email !== value);
    });
  };

  const restEmail = () => {
    handleFormChange({ target: { value: "" } });
    formValidator.current.hideMessages();
    setForceUpdate(0);
  };

  const handleSendEmail = async () => {
    if (state.email?.trim() !== "") {
      const emails = await handleAddEmail({ key: "Enter" });
      emails && handleFormSubmit(emails);
    } else {
      handleFormSubmit(state.emails);
    }
  };

  React.useEffect(() => {
    if (defaultEmails.length > 0) {
      setState((draft) => {
        draft.emails = defaultEmails;
      });
    }
  }, [defaultEmails]);

  //   UseEffect to reset selected emails on close the dialog
  React.useEffect(() => {
    return () => {
      if (state.emails.length > 0) {
        setState((draft) => {
          draft.emails = [];
          draft.email = "";
        });
      }
    };
  }, [open]);

  return {
    state,
    formValidator,
    handleFormChange,
    handleRemoveEmail,
    handleAddEmail,
    handleSendEmail,
  };
};
